import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { merge } from 'lodash';
import { Store } from '@watchable/store';
import { edit } from '@watchable/store-edit';
import { useStore } from '@watchable/store-react';

import { AppDispatchContext } from 'context/app';
import { ConfigContext } from 'context/config';

import { aspenSockets } from 'modules/aspenSockets';
import { formatLeaves, getOrderFromLeaves } from 'modules/leaves';
import { AppLeaf } from 'types/common/leaf';
import { useAspenSocket } from 'hooks/useAspenSocket';

import { AspenLeaf } from 'types/aspen/leaf';
import { AspenSocketMsg } from 'types/aspen/socketMessages';

interface LeavesContext {
  leafStore: Store<Record<string, AppLeaf>>;
  loading: boolean;
  fetchError: Error | null;
  socketError: Error | null;
  socketFallback: boolean;
}

export const LeavesContext = createContext<LeavesContext>({} as LeavesContext);

interface LeavesProviderProps {
  children: React.ReactNode;
}

export const LeavesProvider = ({ children }: LeavesProviderProps) => {
  const { baseUrl } = useContext(ConfigContext);
  const dispatch = useContext(AppDispatchContext);
  const { fetchLeaves } = aspenSockets({ baseUrl });

  const leafStore = useStore<Record<string, AppLeaf>>(null as unknown as Record<string, AppLeaf>);
  const [fetchError, setFetchError] = useState<LeavesContext['fetchError']>(null);
  const [loading, setLoading] = useState<LeavesContext['loading']>(true);
  const [socketError, setSocketError] = useState<LeavesContext['socketError']>(null);
  const [socketFallback, setSocketFallback] = useState(false);

  const { connection, subscribe, unsubscribe, getLeaves } = useAspenSocket();

  const writeStoreAndSetOrder = (leaves: Array<AspenLeaf>) => {
    leafStore.write(formatLeaves(leaves));
    dispatch({ type: 'deviceOrder', payload: getOrderFromLeaves(leaves) });
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const fetchedLeaves = await fetchLeaves();
        if (fetchedLeaves && leafStore.read() === null) {
          writeStoreAndSetOrder(fetchedLeaves);
        }
      } catch (_err) {
        setFetchError(Error('Failed to fetch leaves.'));

        if (socketError || socketFallback) {
          setLoading(false);
        }
      }
    };

    if (leafStore) {
      if (connection) {
        // Fall back to socket message response if http request fails
        subscribe((messageEvent: MessageEvent) => {
          const message: AspenSocketMsg = JSON.parse(messageEvent.data);

          if (connection.readyState === connection.OPEN && message.type === 'init') {
            getLeaves();
          }

          if (leafStore.read() === null) {
            if (connection.readyState === connection.OPEN && message.type === 'init') {
              setLoading(true);
            }

            if (message.type === 'leaves') {
              if (message.payload) {
                writeStoreAndSetOrder(message.payload);
              }
              // This will trigger a render on the whole app
              setSocketFallback(true);
              setLoading(false);
            }
          }
        });

        // Subscribe to updates
        subscribe((messageEvent: MessageEvent) => {
          const message: AspenSocketMsg = JSON.parse(messageEvent.data);

          switch (message.type) {
            case 'leaves':
            case 'leaf_state_report':
              if (message.payload) {
                edit(leafStore, (updatedState) => {
                  merge(updatedState, formatLeaves(message.payload));
                });
              }
              break;
            case 'leaves_changes':
            case 'settings_changes':
              if (message.payload) {
                edit(leafStore, (updatedState) => {
                  merge(updatedState, formatLeaves(message.payload.update));
                });
              }
              break;
            default:
          }
        });

        // Subscribe to socket errors
        connection.onerror = () => {
          if (connection.retryCount === 5) {
            setSocketError(new Error('WebSocket error.'));

            if (fetchError && !socketFallback) {
              setLoading(false);
            }
          }
        };
      }

      fetchData();
    }

    return unsubscribe;
  }, [leafStore, connection]);

  const contextValue = useMemo(
    () => ({ leafStore, loading, fetchError, socketError, socketFallback }),
    [leafStore, loading, fetchError, socketError, socketFallback],
  );

  return <LeavesContext.Provider value={contextValue}>{children}</LeavesContext.Provider>;
};
